/**
 * Field formatting utilities for custom attribute transformations.
 *
 * Provides functions to format field values before display, such as:
 * - Converting city codes to city names
 * - Rearranging address formats
 */

/**
 * Lookup table for city codes to city names.
 * Handles blank/empty values by treating them as "N/A".
 */
const CITY_LOOKUP: Record<string, string> = {
  " ": "N/A",
  "000": "Tipton",
  "036": "Atoka",
  "087": "Brighton",
  "171": "Covington",
  "281": "Garland",
  "467": "Mason",
  "512": "Munford"
};

/**
 * Converts a city number code to the corresponding city name.
 * Returns "N/A" for blank, empty, null, or undefined values.
 * Returns the original value if no mapping is found.
 */
export function formatCityNum(cityNum: any): string {
  if (cityNum == null || cityNum === "" || cityNum === " ") {
    return "N/A";
  }

  const cityCode = String(cityNum).trim();
  return CITY_LOOKUP[cityCode] || cityNum;
}

/**
 * Rearranges an address string to move the street number to the front.
 * Example: "Main St 123" becomes "123 Main St"
 * Returns the original string if the last word is not a number.
 */
export function formatAddress(address: string | null | undefined): string {
  if (!address) return "";

  const parts = address.split(' ');
  if (parts.length < 2) return address;

  const streetNum = parts[parts.length - 1];

  // Check if the last part is a valid number
  if (!/^\d+$/.test(streetNum)) return address;

  const streetName = parts.slice(0, -1).join(' ');
  return `${streetNum} ${streetName}`;
}

/**
 * Main field formatting function that determines which formatter to apply
 * based on the field name.
 */
export function formatField(fieldName: string, fieldValue: any): any {
  switch (fieldName) {
    case "CITYNUM":
      return formatCityNum(fieldValue);
    case "ADDRESS":
      return formatAddress(fieldValue);
    default:
      return fieldValue;
  }
}
